// Module 08 — the per-navigation report reset, contributed to 05's `registerNavReset` (ORP-18644
// AC6, the last open item on that ticket; ORP-18652 AC7 names the same wiring).
//
// Ports v3 `router/index.js:64-73`, whose mega-`beforeEach` ran, in this exact order:
//
//   controller.abort()            // :65  kill the previous navigation's in-flight requests
//   controller = new AbortController(); controllerAxios.defaults.signal = controller.signal
//   clear()                       // :72  empty the report cache (utils/reportCache.js)
//   reportStore.resetRequests()   // :73  zero pageRequests / pageDoneRequests
//
// The order is load-bearing and survives the port unchanged: ABORT STRICTLY BEFORE CLEAR. Reversed,
// a request registered in the window between the clear and the abort is killed while still mapped,
// and the next reader awaits an already-rejected promise.
//
// In v4 that order is STRUCTURAL rather than a statement sequence: `cancelInFlightGuard` is a
// `beforeEach` and this reset runs from `runNavResets`, an `afterEach` (contract 05 §11 fixes the
// guard chain at cancelInFlight → auth → permission). A confirmed navigation therefore always
// aborts before it clears. Registering this reset as a beforeEach ahead of `cancelInFlightGuard`
// would reintroduce exactly the v3 hazard — `app/router/__tests__/reportNavResetOrder.spec.ts`
// locks it.
//
// What is cleared:
//   * the report request cache (`service/reportRequestCache.ts`) — AC6's "clears the cache" literally.
//     This is the direct counterpart of v3's `utils/reportCache.js` `clear()` at `:72`, and the whole
//     reason the ordering above matters: an entry left behind after the abort would be a rejected
//     promise, and the next page would await it.
//   * `reportScheduleStore`'s dropdown-value in-flight map — the feature's other shared-promise
//     cache, whose generation bump also stops a late settle re-registering into the cleared map.
//
// Still absent by design: v3's `pageRequests`/`pageDoneRequests` counters, replaced by
// `useReportData.state`. "Zeroes the counters" has no v4 subject — see the ORP-18644 comment.
import { clearReportRequestCache } from '../service/reportRequestCache'
import { useReportScheduleStore } from '../stores/reportScheduleStore'

export type NavResetRegister = (reset: () => void) => void

/**
 * The store is resolved INSIDE the callback, not at registration time: the shell registers during
 * boot composition, and resolving a Pinia store then would bind to whatever instance was active at
 * that moment (and throw outright if none is active yet).
 */
export function registerReportNavResets(register: NavResetRegister): void {
  register(() => {
    clearReportRequestCache()
    useReportScheduleStore().resetInflightDropdownValues()
  })
}
